import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { POINTS } from '../utils/levelCalculation';
import type { Routine, RoutineStep } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { cloudVersionsOf, createOwnRowSync, marksOf, mergeLoad, ownCache, rowFocusRefresh, type OwnRowSync, type RowMarks } from '../lib/ownRowSync';
import { refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { useGroomingStore } from './groomingStore';

/**
 * A finished run ticks every discipline action the routine drives — the routine
 * counterpart of habitStore's `dispatchDisciplineAction`. Grooming: mark each
 * covered care item done.
 *
 * ⭐ `markDone` sets ONE id on the server's `done_ids` for today (read, change,
 * compare-and-swap — docs/DAY_LOG_STALE_WRITES.md): idempotent, never a flip, and it
 * never re-sends this tab's copy of the day, so it needs no loaded day first. The
 * store queues its writes; awaiting each here just keeps them in routine order.
 */
async function dispatchRoutineDisciplineActions(routine: Routine) {
  const itemIds = (routine.disciplineActions ?? [])
    .filter((a) => a.type === 'grooming' && a.itemId)
    .map((a) => a.itemId as string);
  for (const itemId of itemIds) {
    await useGroomingStore.getState().markDone(itemId);
  }
}

// ─── Supabase sync helpers (sync epic — same pattern as the other stores) ───
// The `routines` table keeps typed columns + nested `steps` jsonb + a `data`
// jsonb holding the full Routine (read source of truth). A load keeps an unsaved
// edit of the routine as the cloud still has it — no clock decides (lib/ownRowSync.ts).
// Writes are fire-and-forget.
// ⚠️⚠️ A DELETED ROUTINE STAYS DELETED (2026-10-18): writes go through `lib/ownRowSync.ts` —
// only routines created here and unconfirmed (`unsyncedIds`) are inserted, every other write
// is an UPDATE (finishing a run, archiving, unassigning a deleted folder's routines…), and a
// routine the cloud no longer has is dropped here, never re-created.

const ROUTINES_TABLE = 'routines';

/** The row's columns, without its id (an UPDATE never sets the id). */
function routineFields(r: Routine, userId: string) {
  return {
    user_id: userId,
    name: r.name,
    description: r.description ?? null,
    category: r.category ?? '',
    steps: r.steps ?? [],
    total_xp: r.totalXP ?? 0,
    total_active_time: r.totalActiveTime ?? 0,
    total_duration: r.totalDuration ?? 0,
    completed_count: r.completedCount ?? 0,
    last_completed_at: r.lastCompletedAt ?? null,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    data: r,
  };
}

function rowToRoutine(row: any): Routine {
  const data = row?.data;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    return data as Routine;
  }
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? undefined,
    category: row.category ?? '',
    steps: row.steps ?? [],
    totalXP: row.total_xp ?? 0,
    totalActiveTime: row.total_active_time ?? 0,
    totalDuration: row.total_duration ?? 0,
    completedCount: row.completed_count ?? 0,
    lastCompletedAt: row.last_completed_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  } as Routine;
}

const routineSync: OwnRowSync<Routine> = createOwnRowSync<Routine>({
  table: ROUTINES_TABLE,
  label: 'routines',
  fields: routineFields,
  current: (id) => useRoutineStore.getState().routines.find((r) => r.id === id),
  fromRow: rowToRoutine,
  replace: (routine) => useRoutineStore.setState((s) => ({ routines: s.routines.map((r) => (r.id === routine.id ? routine : r)) })),
  unsyncedIds: () => useRoutineStore.getState().unsyncedIds,
  confirm: (id) => useRoutineStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s),
  drop: (id) => useRoutineStore.setState((s) => ({
    routines: s.routines.filter((r) => r.id !== id),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
    ...(s.activeRoutineId === id ? { activeRoutineId: null } : {}),
  })),
  marks: () => useRoutineStore.getState(),
  setMarks: (marks) => useRoutineStore.setState(marks),
  name: (r) => r.name,
});

/** Save these routines (each through its own queue — see `lib/ownRowSync.ts`). */
const cloudSave = (routines: Routine | Routine[]): Promise<boolean> => routineSync.save(routines);

async function cloudDelete(id: string): Promise<void> {
  if (!id) return;
  // ⚠️ After any save of this routine already on the wire: a delete that overtook the
  // routine's own insert would be undone by it.
  await routineSync.settle(id);
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase.from(ROUTINES_TABLE).delete().eq('id', id);
    if (error) console.error('Failed to delete routine from Supabase:', error);
  } catch (error) {
    console.error('Error deleting routine from Supabase:', error);
  }
}

interface RoutineState extends RowMarks<Routine> {
  routines: Routine[];
  activeRoutineId: string | null;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** Routines created on THIS device whose insert the cloud hasn't confirmed (see lib/ownRowSync.ts). Persisted. */
  unsyncedIds: string[];
  // `dirtyIds` + `cloudVersions` + `dirtyBases` (RowMarks, persisted): what a load decides by, without a clock — lib/ownRowSync.ts.
  initialize: () => Promise<void>;
  /** Returns the new routine's id. */
  addRoutine: (routine: Omit<Routine, 'id' | 'createdAt' | 'updatedAt' | 'completedCount'>) => string;
  updateRoutine: (id: string, updates: Partial<Routine>) => void;
  deleteRoutine: (id: string) => void;
  completeRoutine: (id: string) => void;
  startRoutine: (id: string) => void;
  stopRoutine: () => void;
  getRoutineById: (id: string) => Routine | undefined;
  getRoutinesByCategory: (category: string) => Routine[];
  addStep: (routineId: string, step: Omit<RoutineStep, 'id' | 'order'>) => void;
  updateStep: (routineId: string, stepId: string, updates: Partial<RoutineStep>) => void;
  deleteStep: (routineId: string, stepId: string) => void;
  reorderSteps: (routineId: string, sourceIndex: number, targetIndex: number) => void;
}

export const useRoutineStore = create<RoutineState>()(
  persist(
    (set, get) => ({
      routines: [],
      activeRoutineId: null,
      initializedUserId: null,
      cachedUserId: null,
      unsyncedIds: [],
      dirtyIds: [],
      cloudVersions: {},
      dirtyBases: {},

      // Load-on-login, keyed by user id (re-runs on account switch). The cloud's
      // copy wins except an unsaved edit of the routine (merged onto it if written since);
      // a cached routine the cloud didn't return was deleted elsewhere and goes,
      // unless it is unsynced (lib/ownRowSync.ts mergeLoad); cross-account cache guarded.
      initialize: () => dedupeInit('routines', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('routines', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { result: { data, error }, win } = await routineSync.load(() => supabase
            .from(ROUTINES_TABLE)
            .select('*'));
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const cloudRoutines = (data ?? []).map(rowToRoutine);

          let kept: string[] = [];
          set((state) => {
            const merged = mergeLoad({
              ...ownCache(state.cachedUserId, user.id, state.routines, state.unsyncedIds),
              marks: marksOf(state),
              cloud: cloudRoutines,
              versions: cloudVersionsOf(data ?? []),
              win,
              label: 'routines',
            });
            kept = merged.kept;
            return {
              routines: merged.rows,
              unsyncedIds: merged.unsyncedIds,
              ...merged.marks,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          routineSync.seen(cloudRoutines, win); // the base of each routine's next edit (lib/ownRowSync.ts)
          void routineSync.writeBack(kept); // what the merge kept over the cloud (unsynced, or an unsaved edit)
          markCloudFresh('routines', user.id);
        } catch (error) {
          console.error('Failed to initialize routines from Supabase:', error);
        }
      }),

      addRoutine: (routineData) => {
        const routine: Routine = {
          ...routineData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          completedCount: 0,
          totalXP: calculateTotalXP(routineData.steps),
          totalActiveTime: calculateTotalActiveTime(routineData.steps),
          totalDuration: calculateTotalDuration(routineData.steps)
        };

        set((state) => ({
          routines: [...state.routines, routine],
          unsyncedIds: [...state.unsyncedIds, routine.id], // ← INSERTED until the cloud confirms it
        }));

        void cloudSave(routine);
        return routine.id;
      },

      updateRoutine: (id, updates) => {
        set((state) => ({
          routines: state.routines.map((routine) => {
            if (routine.id !== id) return routine;

            const updatedRoutine = {
              ...routine,
              ...updates,
              updatedAt: new Date().toISOString()
            };

            // Recalculate totals if steps were updated
            if (updates.steps) {
              updatedRoutine.totalXP = calculateTotalXP(updates.steps);
              updatedRoutine.totalActiveTime = calculateTotalActiveTime(updates.steps);
              updatedRoutine.totalDuration = calculateTotalDuration(updates.steps);
            }

            return updatedRoutine;
          })
        }));

        const updated = get().routines.find((r) => r.id === id);
        if (updated) void cloudSave(updated);
      },

      deleteRoutine: (id) => {
        routineSync.noteDeleted(id);
        set((state) => ({
          routines: state.routines.filter((routine) => routine.id !== id),
          unsyncedIds: state.unsyncedIds.filter((x) => x !== id),
        }));

        void cloudDelete(id);
      },

      completeRoutine: (id) => {
        set((state) => ({
          routines: state.routines.map((routine) =>
            routine.id === id
              ? {
                  ...routine,
                  completedCount: routine.completedCount + 1,
                  lastCompletedAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              : routine
          )
        }));

        const completed = get().routines.find((r) => r.id === id);
        if (completed) {
          void cloudSave(completed);
          void dispatchRoutineDisciplineActions(completed);
        }
      },
      
      startRoutine: (id) => {
        set({ activeRoutineId: id });
      },
      
      stopRoutine: () => {
        set({ activeRoutineId: null });
      },

      getRoutineById: (id) => {
        return get().routines.find((routine) => routine.id === id);
      },

      getRoutinesByCategory: (category) => {
        return get().routines.filter((routine) => routine.category === category);
      },

      addStep: (routineId, stepData) => {
        set((state) => {
          const routine = state.routines.find((r) => r.id === routineId);
          if (!routine) return state;

          const newStep: RoutineStep = {
            ...stepData,
            id: crypto.randomUUID(),
            order: routine.steps.length
          };

          const updatedSteps = [...routine.steps, newStep];

          return {
            routines: state.routines.map((r) =>
              r.id === routineId
                ? {
                    ...r,
                    steps: updatedSteps,
                    totalXP: calculateTotalXP(updatedSteps),
                    totalActiveTime: calculateTotalActiveTime(updatedSteps),
                    totalDuration: calculateTotalDuration(updatedSteps),
                    updatedAt: new Date().toISOString()
                  }
                : r
            )
          };
        });

        const r = get().routines.find((x) => x.id === routineId);
        if (r) void cloudSave(r);
      },

      updateStep: (routineId, stepId, updates) => {
        set((state) => {
          const routine = state.routines.find((r) => r.id === routineId);
          if (!routine) return state;

          const updatedSteps = routine.steps.map((step) =>
            step.id === stepId ? { ...step, ...updates } : step
          );

          return {
            routines: state.routines.map((r) =>
              r.id === routineId
                ? {
                    ...r,
                    steps: updatedSteps,
                    totalXP: calculateTotalXP(updatedSteps),
                    totalActiveTime: calculateTotalActiveTime(updatedSteps),
                    totalDuration: calculateTotalDuration(updatedSteps),
                    updatedAt: new Date().toISOString()
                  }
                : r
            )
          };
        });

        const r = get().routines.find((x) => x.id === routineId);
        if (r) void cloudSave(r);
      },

      deleteStep: (routineId, stepId) => {
        set((state) => {
          const routine = state.routines.find((r) => r.id === routineId);
          if (!routine) return state;

          const updatedSteps = routine.steps
            .filter((step) => step.id !== stepId)
            .map((step, index) => ({ ...step, order: index }));

          return {
            routines: state.routines.map((r) =>
              r.id === routineId
                ? {
                    ...r,
                    steps: updatedSteps,
                    totalXP: calculateTotalXP(updatedSteps),
                    totalActiveTime: calculateTotalActiveTime(updatedSteps),
                    totalDuration: calculateTotalDuration(updatedSteps),
                    updatedAt: new Date().toISOString()
                  }
                : r
            )
          };
        });

        const r = get().routines.find((x) => x.id === routineId);
        if (r) void cloudSave(r);
      },

      reorderSteps: (routineId, sourceIndex, targetIndex) => {
        set((state) => {
          const routine = state.routines.find((r) => r.id === routineId);
          if (!routine) return state;

          const steps = [...routine.steps];
          const [removed] = steps.splice(sourceIndex, 1);
          steps.splice(targetIndex, 0, removed);

          const updatedSteps = steps.map((step, index) => ({
            ...step,
            order: index
          }));

          return {
            routines: state.routines.map((r) =>
              r.id === routineId
                ? {
                    ...r,
                    steps: updatedSteps,
                    updatedAt: new Date().toISOString()
                  }
                : r
            )
          };
        });

        const r = get().routines.find((x) => x.id === routineId);
        if (r) void cloudSave(r);
      }
    }),
    {
      name: 'routines-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      // `initializedUserId` + the transient `activeRoutineId` stay out of
      // persistence; load-on-login runs fresh each start.
      partialize: (state: RoutineState) => ({
        routines: state.routines,
        cachedUserId: state.cachedUserId,
        // ⚠️ A cache from before this field has none: all cloud copies (ghosts go on load).
        unsyncedIds: state.unsyncedIds,
        // ⚠️ A cache from before these has no marks: no unsaved edit, so its first load takes the cloud's copies.
        dirtyIds: state.dirtyIds,
        cloudVersions: state.cloudVersions,
        // ⚠️ Each unsaved row's base: without it a load drops that edit (lib/ownRowSync.ts). A cache from
        // before it has none.
        dirtyBases: state.dirtyBases,
      }),
      // The saved copy read back (a page load, a change of account): the base of each routine's next
      // edit — as this device last saw it in the cloud; an unsaved one's, its saved base (lib/ownRowSync.ts
      // `restore`). ⚠️ Runs inside `create`, before `useRoutineStore` exists: the marks come with `state`.
      onRehydrateStorage: () => (state) => { if (state) routineSync.restore(state.routines, state); },
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's routines until its own load; routines that lived only here were replaced by the next account's
   load; and the next account's routines made before its own load were kept by the last one's. */
scopeToAccount(useRoutineStore);

// ⭐ A tab that comes back shows the routines the cloud holds now — read only (lib/ownRowSync.ts `rowFocusRefresh`).
refreshBlobOnFocus(ROUTINES_TABLE, rowFocusRefresh(routineSync, {
  label: 'routines',
  loadedFor: () => useRoutineStore.getState().initializedUserId,
  held: () => useRoutineStore.getState().routines,
  versions: () => supabase.from(ROUTINES_TABLE).select('id, updated_at'),
  apply: (read) => useRoutineStore.setState((state) => {
    const merged = mergeLoad({ cached: state.routines, unsyncedIds: state.unsyncedIds, marks: marksOf(state), ...read.over(state.routines), label: 'routines' });
    return { routines: merged.rows, unsyncedIds: merged.unsyncedIds, ...merged.marks };
  }),
}));

// Helper functions for calculations
function calculateTotalXP(steps: RoutineStep[]): number {
  return steps.reduce((total, step) => {
    if (step.type === 'habit' || step.type === 'folder' || step.type === 'prayer') {
      return total + (step.duration * POINTS.ROUTINE_MINUTE);
    }
    return total;
  }, 0);
}

function calculateTotalActiveTime(steps: RoutineStep[]): number {
  return steps.reduce((total, step) => {
    if (step.type === 'habit' || step.type === 'folder' || step.type === 'prayer') {
      return total + step.duration;
    }
    return total;
  }, 0);
}

function calculateTotalDuration(steps: RoutineStep[]): number {
  return steps.reduce((total, step) => total + step.duration, 0);
}